import React, { useState } from "react";
import { IMissingDataResponse } from "../MissingData/MissingDataBuyer";
import { useParams } from "react-router-dom";
import { cpfMask } from "utils/cpf";
import dayjs from "dayjs";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { ptBR } from '@mui/x-data-grid/locales';
import { Box, Button, CircularProgress, IconButton, Modal, Table, TableCell, TableContainer, TableRow } from "@mui/material";
import { ArrowBack, Close } from "@mui/icons-material";
import api from "services/api";

interface IProps {
  data: IMissingDataResponse[];
  setIsEdit: (payload: boolean) => void;
}

export function EditarDadosFaltantes({ data, setIsEdit }: IProps) {
  const { workspaceId } = useParams() as { workspaceId: string };
  const [changes, setChanges] = useState<null | { id: string, name: string, taxPayerId: string, type: string, motherName: { newer: string, old: string, field: string }, weddingDate: { newer: string, old: string, field: string }, personName: { newer: string, old: string, field: string }}[]>(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSendingUpdate, setIsSendingUpdate] = useState(false);

  const [realRows, setRealRows] = useState(data.map((row, index) => ({
    id: `${index + 1}_${Date.now()}`,
    name: row?.PERSON_NAME ?? '-',
    taxPayerId: !!row?.PERSON_TAX_PAYER_ID ? cpfMask(row.PERSON_TAX_PAYER_ID) : '-',
    type: row?.PERSON_TYPE === "employee" ? "Titular" : 'Dependente',
    motherName: row?.EMPLOYEE_MOTHER_NAME ? row.EMPLOYEE_MOTHER_NAME : '-',
    weddingDate: null,
    birthDate: !!row?.PERSON_BIRTH_DATE ? dayjs(row.PERSON_BIRTH_DATE).format("DD/MM/YYYY") : '-',
  })));
  
  const realColumns: GridColDef<(typeof realRows)[number]>[] = [
    { field: "name", headerName: "Nome completo", width: 350, editable: true },
    { field: "taxPayerId", headerName: "CPF beneficiário", width: 150 },
    { field: "type", headerName: "Tipo dependente", width: 200 },
    { field: "motherName", headerName: "Nome da mãe", width: 200, editable: true },
    { field: "weddingDate", headerName: "Data de casamento", width: 200, editable: true, type: "date" },
    { field: "birthDate", headerName: "Data de nascimento", width: 200 },
  ];

  async function handleSend() {
    try {
      setIsSendingUpdate(true);
      if (!changes) throw new Error("Nenhuma mudança identificada.");

      const payload = changes.map(change => ({
        name: change?.personName?.newer ?? undefined,
        weddingDate: change?.weddingDate?.newer ?? undefined,
        motherName: change?.motherName?.newer ?? undefined,
        taxPayerId: change?.taxPayerId?.replace(/\D/g, ""),
        personType: change?.type === "Titular" ? "employee" : "dependent"
      }));

      console.log("!!!", payload)

      await api.post(`/v2/workspaces/${workspaceId}/missing-data-update`, {
        data: payload,
      })
    } catch (error) {
      alert(error)
    } finally {
      setIsSendingUpdate(false);
      setIsModalOpen(false);
      setIsEdit(false);
      //redirecionar de página (passar useState q deixa isEditing false)
    }
  }

  function handleUndo() {
    setRealRows(data.map((row, index) => ({
      id: `${index + 1}_${Date.now()}`,
      name: row?.PERSON_NAME ?? '-',
      taxPayerId: !!row?.PERSON_TAX_PAYER_ID ? cpfMask(row.PERSON_TAX_PAYER_ID) : '-',
      type: row?.PERSON_TYPE === "employee" ? "Titular" : 'Dependente',
      motherName: row?.EMPLOYEE_MOTHER_NAME ? row.EMPLOYEE_MOTHER_NAME : '-',
      weddingDate: null,
      birthDate: !!row?.PERSON_BIRTH_DATE ? dayjs(row.PERSON_BIRTH_DATE).format("DD/MM/YYYY") : '-',
    })));
    setChanges(null);
  }

  return (
    <>
      <Modal open={isModalOpen} aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description">
        <Box sx={{ position: "absolute" as "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", height: "auto", width: "100%", maxWidth: 1000, bgcolor: "background.paper", boxShadow: 24, borderRadius: "1rem", border: "none", outline: "none", padding: "24px" }}>
          <div style={{"display":"flex","justifyContent":"space-between","alignItems":"center"}}>
            <h6 style={{"padding":"0","margin":"0","fontSize":"1.25rem","lineHeight":"1.75rem","fontWeight":600}}>Salvar alterações?</h6>
            <Close style={{"cursor":"pointer"}} onClick={() => setIsModalOpen(false)}/>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '14px', marginTop: '24px' }}>
            <span style={{ fontSize: '16px', lineHeight: '175%', fontWeight: '400', color: '#191C1A' }}>Por favor, confira os dados antes de confirmar. As informações anteriores serão substituídas.</span>
          </div>
          <TableContainer sx={{ "& .MuiTableCell-root": { background: "#FFFFFF !important" } }}>
            <Table>
              <TableRow style={{ height: "70px" }}>
                <TableCell>
                  <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: "rgb(111, 111, 120)", fontWeight: "400"}}>Nome completo</span>
                </TableCell>
                <TableCell>
                  <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: "rgb(111, 111, 120)", fontWeight: "400" }}>CPF beneficiário</span>
                </TableCell>
                <TableCell>
                  <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: "rgb(111, 111, 120)", fontWeight: "400" }}>Campos editados</span>
                </TableCell>
              </TableRow>
              {changes && changes.map(row => (
                <TableRow key={row.id} style={{ height: "70px" }}>
                  <TableCell>{row.name}</TableCell>
                  <TableCell>{row.taxPayerId}</TableCell>
                  <TableCell>
                    {[row?.motherName?.field ?? null, row?.weddingDate?.field ?? null, row?.personName?.field ?? null].filter(r => r !== null).join(", ")}
                  </TableCell>
                </TableRow>
              ))}
            </Table>
          </TableContainer>
          <div style={{"display":"flex","paddingTop":"0.5rem","paddingBottom":"0.5rem","marginTop":"1rem","justifyContent":"flex-end","alignItems":"center","width":"100%"}}>
            <div style={{"display":"flex","gap":"0.25rem"}}>
              <Button
                variant='outlined'
                color='error'
                style={{"paddingTop":"0.25rem","paddingBottom":"0.25rem","paddingLeft":"1rem","paddingRight":"1rem","borderRadius":"0.375rem","height":"2.5rem","fontSize":"0.875rem","lineHeight":"1.25rem","fontWeight":600}}
                onClick={() => setIsModalOpen(false)}
                disabled={isSendingUpdate}
              >
                Cancelar
              </Button>

              <Button
                variant='contained'
                style={{ minWidth: "180px", "paddingTop":"0.25rem","paddingBottom":"0.25rem","paddingLeft":"1rem","paddingRight":"1rem","borderRadius":"0.375rem","height":"2.5rem","fontSize":"0.875rem","lineHeight":"1.25rem","fontWeight":600}}
                onClick={handleSend}
                disabled={isSendingUpdate}
              >
                {!isSendingUpdate ? "Confirmar e Salvar" : <CircularProgress size="20px"/>}
              </Button>
            </div>
          </div>
        </Box>
      </Modal>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <IconButton>
          <ArrowBack color="primary" style={{ width: "25px" }} onClick={() => setIsEdit(false)}/>
        </IconButton>
        <h1 style={{"padding":"0","marginTop":"12px","fontSize":"1.25rem","lineHeight":"1.75rem","fontWeight":600}}>Editar dados</h1>
      </div>
      <DataGrid
        rows={realRows}
        columns={realColumns}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 10,
            },
          },
        }}
        pageSizeOptions={[5, 10, 20]}
        disableRowSelectionOnClick
        style={{ backgroundColor: "white" }}
        editMode="row"
        //@ts-ignore
        processRowUpdate={(updatedRow, originalRow) => {
          const newArr = [...realRows];
          const idx = newArr.findIndex(r => r.id === updatedRow.id)
          newArr[idx] = { ...updatedRow };

          const diffs = newArr.map(r1 => {
            const r2 = data.find(row => row?.PERSON_TAX_PAYER_ID?.replace(/\D/g, "") === r1?.taxPayerId?.replace(/\D/g, ""));
            if (!r2) return null;
            const changes: any = { id: r1.id, name: r1.name, taxPayerId: r1.taxPayerId, type: r1.type };
            if (r1.motherName !== '-' && r1.motherName !== r2.EMPLOYEE_MOTHER_NAME) {
              changes.motherName = { old: r2.EMPLOYEE_MOTHER_NAME, newer: r1.motherName, field: "Nome da mãe" };
            };
            if (r1.name !== '-' && r1.name !== r2.PERSON_NAME) {
              changes.personName = { old: r2.PERSON_NAME, newer: r1.name, field: "Nome completo" };
            };
            if (!!r1.weddingDate) {
              changes.weddingDate = { old: null, newer: r1.weddingDate, field: "Data de casamento" };
            }

            return Object.keys(changes).length > 4 ? changes : null;
          }).filter(d => d !== null);
          setChanges(diffs);

          setRealRows(o => [...newArr])
        }}
        onProcessRowUpdateError={() => {}}
        localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
      />
      <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'end', gap: "10px","paddingTop":"0.5rem","paddingBottom":"0.5rem","marginTop":"24px" }}>
        <Button
          variant='outlined'
          style={{"paddingTop":"0.25rem","paddingBottom":"0.25rem","paddingLeft":"1rem","paddingRight":"1rem","borderRadius":"0.375rem","height":"2.5rem","fontSize":"0.875rem","lineHeight":"1.25rem","fontWeight":600}}
          onClick={() => handleUndo()}
          disabled={!changes}
        >
          Descartar alterações
        </Button>
        <Button
          variant='contained'
          style={{"paddingTop":"0.25rem","paddingBottom":"0.25rem","paddingLeft":"1rem","paddingRight":"1rem","borderRadius":"0.375rem","height":"2.5rem","fontSize":"0.875rem","lineHeight":"1.25rem","fontWeight":600}}
          onClick={() => setIsModalOpen(true)}
          disabled={!changes}
        >
          Salvar
        </Button>
      </div>
    </>
  )
}
